import { type ProductCardProduct } from '@/features/shop/components/ProductCard/types'
import { getCouponDetails } from '@/features/shop/utils/normalizeCoupon'
import {
  getCouponPromoCardProperties,
  trackPromoCardClicked,
} from '@/features/shared/analytics/promoCard'
import styled from 'styled-components'
import { Body } from '@shipt/design-system-typography'
import { PositiveIcon, DealIcon } from '@shipt/design-system-icons'
import { ProductPromotionContainer } from '@/features/shop/components/ProductCard/price/ProductPromotionContainer'
import { useCouponSavings } from '@/features/shop/services/Coupons/hooks'
import { type Coupon } from '@/features/shop/services/Coupons/types'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicCouponModal } from '@/features/shop/components/Modal/CouponModal/DynamicCouponModal'
import { useIsGuestUser } from '@/features/account/services/User/hooks'
import { usePathWithSearch } from '@/features/shared/hooks/routes/usePathWithSearch'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import { routes } from '@shared/constants/routes'

type Props = {
  product: ProductCardProduct
  coupon: Coupon
}

export const ProductCardCoupon = ({ product, coupon }: Props) => {
  const { openDialog } = useHandleDialog()
  const { appliedProductQty = 0 } = coupon
  const { isClipped, isFulfilled } = useCouponSavings(coupon)
  const { formattedValue } = getCouponDetails(coupon)
  const isGuestUser = useIsGuestUser()
  const exactPath = usePathWithSearch()
  const encodedFullPath = encodeURIComponent(exactPath)
  const router = useSegwayRouter()

  const promotionText = `Buy ${appliedProductQty}, Get ${formattedValue} Off`
  const couponText = `Save ${formattedValue}`

  const formattedText = appliedProductQty > 1 ? promotionText : couponText

  const handleOpenCoupons = (e: React.MouseEvent) => {
    if (!isClipped) {
      e.preventDefault()
      e.stopPropagation()
      if (isGuestUser) {
        router.push(`${routes.SIGN_UP.url}?next=${encodedFullPath}`)
        return
      }
      trackPromoCardClicked(
        getCouponPromoCardProperties({
          coupon,
          actionType: 'coupon',
        }),
      )
      openDialog(DynamicCouponModal, { product, coupon })
    }
  }

  if (isFulfilled) {
    return (
      <CouponPromotionContainer size="sm">
        <CheckIconWrapper>
          <CallOutCheckIcon size="sm" />
          <CouponAppliedText>Coupon applied</CouponAppliedText>
        </CheckIconWrapper>
      </CouponPromotionContainer>
    )
  }

  if (isClipped) {
    return (
      <CouponPromotionContainer size="sm">
        <CouponSaleFilledIcon inherit size="sm" />
        <span>{formattedText}</span>
      </CouponPromotionContainer>
    )
  }

  return (
    <ClipCouponButton
      onClick={handleOpenCoupons}
      aria-label={`${formattedText}, open coupon modal`}
    >
      <CouponButtonText size="sm" strong concept="feedback" feedbackType="promotion">
        {formattedText}
      </CouponButtonText>
    </ClipCouponButton>
  )
}

const CheckIconWrapper = styled.div`
  display: flex;
  align-items: center;
`

const CouponAppliedText = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const CouponSaleFilledIcon = styled(DealIcon)`
  margin-right: 4px;
  vertical-align: middle;
  color: ${({ theme }) => theme.color.content.feedback.promotion.primary_on_soft};
`

const CouponPromotionContainer = styled(ProductPromotionContainer)`
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
`

const ClipCouponButton = styled(TransparentButton)`
  border-color: ${({ theme }) => theme.color.border.base.neutral.soft};
  border-style: dashed;
  border-width: 0.0625rem;
  color: ${({ theme }) => theme.color.content.feedback.promotion.primary_on_soft};
  flex-shrink: 0;
  max-width: 100%;
  border-radius: 0.125rem;
  padding: 0.25rem 0.5rem;

  /* accounts for border spacing to align product name
    with regular promotion spacing */
  margin: 0.375rem 0;

  /* per Target a11y: the typical keyboard outline does not suffice/looks too similar */
  &:focus-visible {
    margin: 0.375rem;
    outline: ${({ theme }) => theme.black} solid 2px;
  }

  p {
    color: inherit;
  }
`

const CouponButtonText = styled(Body)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const CallOutCheckIcon = styled(PositiveIcon)`
  color: inherit;
  margin-right: 0.3rem;
  flex-shrink: 0;
`
